import React from 'react';
import {
  ArrowUpload20Regular,
  Beaker20Regular,
  CalendarClock20Regular,
  DataBarVertical20Regular,
  DocumentBulletList20Regular,
  Link20Regular,
  ReceiptMoney20Regular,
} from '@fluentui/react-icons';
import { WIDGET_KINDS, WidgetKind, WidgetProps } from './Dashboard.Types';
import { SettlementsSummaryWidget } from './widgets/SettlementsSummary.Widget';
import { PendingSettlementsWidget } from './widgets/PendingSettlements.Widget';
import { PinnedContractsWidget } from './widgets/PinnedContracts.Widget';
import { ExpiringContractsWidget } from './widgets/ExpiringContracts.Widget';
import { ImportJobsWidget } from './widgets/ImportJobs.Widget';
import { QuickLinksWidget } from './widgets/QuickLinks.Widget';
import { ActiveProtocolsWidget } from './widgets/ActiveProtocols.Widget';

export interface WidgetDefinition {
  kind: WidgetKind;
  icon: React.ReactNode;
  /** Claves de i18n bajo `dashboard.widgets.<kind>`. */
  titleKey: string;
  descriptionKey: string;
  /** Tiene configuración propia (engranaje en el marco). */
  hasSettings: boolean;
  /** "Ver todo" al pie: ruta y clave de i18n. */
  link?: { to: string; labelKey: string };
  component: React.ComponentType<WidgetProps>;
}

const define = (kind: WidgetKind, icon: React.ReactNode, component: React.ComponentType<WidgetProps>, options: { hasSettings?: boolean; link?: WidgetDefinition['link'] } = {}): WidgetDefinition => ({
  kind,
  icon,
  titleKey: `dashboard.widgets.${kind}.title`,
  descriptionKey: `dashboard.widgets.${kind}.description`,
  hasSettings: !!options.hasSettings,
  link: options.link,
  component,
});

/** Catálogo de widgets disponibles, en el orden en que se ofrecen al agregar. */
export const WIDGET_DEFINITIONS: Record<WidgetKind, WidgetDefinition> = {
  settlementsSummary: define('settlementsSummary', <DataBarVertical20Regular />, SettlementsSummaryWidget, {
    link: { to: '/settlements/', labelKey: 'dashboard.widgets.settlementsSummary.seeAll' },
  }),
  pendingSettlements: define('pendingSettlements', <ReceiptMoney20Regular />, PendingSettlementsWidget, {
    link: { to: '/settlements/', labelKey: 'dashboard.widgets.pendingSettlements.seeAll' },
  }),
  pinnedContracts: define('pinnedContracts', <DocumentBulletList20Regular />, PinnedContractsWidget, {
    hasSettings: true,
    link: { to: '/contracts', labelKey: 'dashboard.widgets.pinnedContracts.seeAll' },
  }),
  expiringContracts: define('expiringContracts', <CalendarClock20Regular />, ExpiringContractsWidget, {
    link: { to: '/contracts', labelKey: 'dashboard.widgets.expiringContracts.seeAll' },
  }),
  importJobs: define('importJobs', <ArrowUpload20Regular />, ImportJobsWidget, {
    link: { to: '/Providers', labelKey: 'dashboard.widgets.importJobs.seeAll' },
  }),
  quickLinks: define('quickLinks', <Link20Regular />, QuickLinksWidget, { hasSettings: true }),
  activeProtocols: define('activeProtocols', <Beaker20Regular />, ActiveProtocolsWidget, {
    link: { to: '/protocolsagreements/', labelKey: 'dashboard.widgets.activeProtocols.seeAll' },
  }),
};

export const widgetCatalog = (): WidgetDefinition[] => WIDGET_KINDS.map((kind) => WIDGET_DEFINITIONS[kind]);
